import { VEHICLE_STYLE_COUNT, VehicleMode, type Vec2, type VehicleResult, type VehicleResultCode } from "@townsy/contracts";
import { canEnterVehicle, driverDoorPoint, vehicleObb, type Obb } from "@townsy/world";
import type { VehicleVisual } from "../scene/types";
import type { VehicleStateLike } from "./connector";

/**
 * Shared town cars on the client (ADR-0006): a validated copy of the public room state, the visual
 * the renderer draws, the "Get in" candidate and the car obstacles walking prediction collides
 * with. Everything here is derived from server state; nothing grants anything (cars are never
 * owned, rewarded or persisted).
 */
export interface VehicleSnapshot {
  vehicleId: string;
  style: number;
  color: number;
  mode: VehicleMode;
  x: number;
  z: number;
  heading: number;
  speed: number;
  driverActorId: string;
  lastSeq: number;
}

const finite = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

/**
 * Defensive copy of a room-state car: unknown modes count as traffic, non-finite numbers as 0, a
 * style outside 0..VEHICLE_STYLE_COUNT-1 as the sedan. Returns null without an id.
 */
export function toVehicleSnapshot(raw: Partial<VehicleStateLike> | null | undefined, key = ""): VehicleSnapshot | null {
  if (!raw) return null;
  const vehicleId = typeof raw.vehicleId === "string" && raw.vehicleId ? raw.vehicleId : key;
  if (!vehicleId) return null;
  const mode = VehicleMode.safeParse(raw.mode);
  const style = finite(raw.style);
  const lastSeq = finite(raw.lastSeq);
  return {
    vehicleId,
    style: Number.isInteger(style) && style >= 0 && style < VEHICLE_STYLE_COUNT ? style : 0,
    color: Math.max(0, Math.floor(finite(raw.color))),
    mode: mode.success ? mode.data : "traffic",
    x: finite(raw.x),
    z: finite(raw.z),
    heading: finite(raw.heading),
    speed: finite(raw.speed),
    driverActorId: typeof raw.driverActorId === "string" ? raw.driverActorId : "",
    lastSeq: lastSeq >= 0 ? Math.floor(lastSeq) : 0,
  };
}

/** The renderer's view of a car. `pose` overrides the position for the car the local player drives (prediction). */
export function vehicleVisual(v: VehicleSnapshot, isLocal: boolean, pose?: { x: number; z: number; heading: number; speed: number }): VehicleVisual {
  return {
    vehicleId: v.vehicleId,
    style: v.style,
    color: v.color,
    mode: v.mode,
    x: pose?.x ?? v.x,
    z: pose?.z ?? v.z,
    heading: pose?.heading ?? v.heading,
    speed: pose?.speed ?? v.speed,
    driverActorId: v.driverActorId,
    isLocal,
  };
}

/** Oriented boxes of the cars a walker collides with (every car on the street, as the server does). */
export function carObstacles(vehicles: Iterable<VehicleSnapshot>, excludeVehicleId: string | null = null): Obb[] {
  const out: Obb[] = [];
  for (const v of vehicles) if (v.vehicleId !== excludeVehicleId) out.push(vehicleObb(v.style, v));
  return out;
}

/**
 * The car the local walker could get into right now: not driven by anyone, stopped, and the walker
 * is at its driver's door (`canEnterVehicle`, the same rule the server checks). The nearest door
 * wins. Occupancy and rate limits stay the server's decision.
 */
export function enterableVehicle(walker: Vec2, vehicles: Iterable<VehicleSnapshot>): VehicleSnapshot | null {
  let best: VehicleSnapshot | null = null;
  let bestDistance = Infinity;
  for (const v of vehicles) {
    if (v.driverActorId || v.mode === "driven") continue;
    if (!canEnterVehicle(walker, v.style, v)) continue;
    const door = driverDoorPoint(v.style, v);
    const d = Math.hypot(walker.x - door.x, walker.z - door.z);
    if (d < bestDistance) {
      best = v;
      bestDistance = d;
    }
  }
  return best;
}

/** Doors, building entry and fast travel wait until the visitor is out of the car (ADR-0006 #4). */
export const OUT_OF_CAR_FIRST = "Get out of the car first: press E or use Get out.";

/** Plain-language explanation for every vehicle.result code (the server's text is not shown). */
export const VEHICLE_RESULT_TEXT: Record<VehicleResultCode, string> = {
  ENTERED: "You're driving. W/S or the arrow keys: gas and brake (hold S to reverse). A/D: steer. E or Get out: leave the car.",
  EXITED: "You got out of the car. It stays parked here for a while, then rejoins traffic.",
  TOO_FAR: "Walk up to the driver's door (the car's left side) to get in.",
  MOVING: "That car is still moving. Wait until it has stopped.",
  OCCUPIED: "Someone else is already driving that car.",
  ALREADY_DRIVING: "You're already driving a car.",
  NO_EXIT_SPACE: "There's no free space to step out here. Move the car a little and try again.",
  NOT_DRIVING: "You're not driving a car.",
  UNKNOWN_VEHICLE: "That car isn't here any more.",
  RATE_LIMITED: "Please wait a moment before getting in or out again.",
  NOT_AVAILABLE: "Cars can only be driven on the street.",
};

export function vehicleResultMessage(result: Pick<VehicleResult, "code">): string {
  return VEHICLE_RESULT_TEXT[result.code] ?? "The car didn't respond. Try again.";
}

/** Toast tone for a result: refusals are warnings, success is informational. */
export function vehicleResultTone(result: Pick<VehicleResult, "ok">): "info" | "warning" {
  return result.ok ? "info" : "warning";
}
